import React, { useState, useEffect, useCallback } from "react";
import { X, MapPin, DollarSign, Home, Users, Percent, StickyNote, Thermometer, Database, CheckCircle2, Store } from "lucide-react";
import MarkLostMenu from "./MarkLostMenu";
import { getAgents } from "../../../api/agent";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";
import AppraisalReminderSeries from "./AppraisalReminderSeries";
import { TEMPERATURES, labelFor as temperatureLabel } from "./AppraisalTemperatureModal";
import {
  formatMelbourneDatePart,
  formatMelbourneTimePart,
  formatMelbourneDateTime,
  datetimeLocalToUtcISOString,
} from "../../../utils/melbourneTime";

// Strip $ and commas, return a clean numeric string ("" if empty/invalid).
const cleanNumber = (val) => {
    if (val == null) return "";
    const s = String(val).replace(/[$,\s]/g, "");
    return s === "" ? "" : s;
};

const methodOptions = [
    { label: "Private Sale", value: "Private Sale" },
    { label: "Auction", value: "Auction" },
    { label: "Expression of Interest", value: "Expression of Interest" },
    { label: "Tender", value: "Tender" },
];

/**
 * The two appraisal pipelines. An appraisal is filed into one of these when it
 * is created, and until now nothing in the CRM could move it to the other one:
 * the Off Market / On Market boards read `appraisal_market_type` and there was
 * no control anywhere that wrote it after creation.
 *
 * "Once an appraisal is loaded as an on-market or off-market appraisal you
 * should be able to move it if things change. Put a toggle here so you can move
 * to on market or off market" (Simon Abbott, CRM-2026-00075, Monday
 * 13099869358, 22 Sep 2026) — his screenshot points at this modal, beside the
 * temperature tiles.
 *
 * Exported so tests and any future mover use one definition rather than a copy
 * that can drift, the same way TEMPERATURES is shared with the Move dialog.
 */
export const PIPELINES = [
    {
        value: "off_market",
        label: "Off Market",
        hint: "Not advertised yet",
        active: "border-[#55C5D0] bg-[#E3F8FA] text-[#1B9AA6]",
        idle: "border-[#E4E6E9] bg-white text-[#6B7280] hover:border-[#55C5D0]",
    },
    {
        value: "on_market",
        label: "On Market",
        hint: "Being advertised",
        active: "border-[#4BCF0E] bg-[#EBFFF2] text-[#15A34A]",
        idle: "border-[#E4E6E9] bg-white text-[#6B7280] hover:border-[#4BCF0E]",
    },
];

// An appraisal with no market type set has never been classified; it shows on
// BOTH boards rather than vanishing, so "Unassigned" is an honest label for it.
export const pipelineLabel = (value) =>
    PIPELINES.find((p) => p.value === String(value || "").toLowerCase())?.label || "Unassigned";

const priceDisplayOptions = [
    { label: "Asking Price", value: "exact" },
    { label: "Price Range", value: "range" },
    { label: "Contact Agent", value: "contact" },
    { label: "Auction / Guided Price", value: "auction" },
];

const labelStyle = {
    fontFamily: "var(--font-sans)",
    fontWeight: 400,
    fontSize: "13.69px",
    lineHeight: "100%",
    color: "var(--c-on-muted)",
    verticalAlign: "middle",
};

const headingStyle = {
    fontFamily: "var(--font-sans)",
    fontWeight: 700,
    fontSize: "17.16px",
    lineHeight: "100%",
    color: "var(--c-on-strong)",
    verticalAlign: "middle",
};

const inputClass =
    "w-full bg-white border border-[#E4E6E9] rounded-[2.81px] px-4 py-3 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 transition-all placeholder:text-[#9CA3AF]";

/**
 * Quick-edit modal for an appraisal in the Off/On Market pipeline.
 * Lets an agent fix the key info shown on the pipeline card (address, price,
 * beds/baths/parking, weekly rent, method of sale, primary agent, commission)
 * without leaving the board. Persists via the property PUT endpoint (partial).
 *
 * It also carries the two actions that until now only lived on the card and the
 * row: change the temperature, and file the appraisal back into the Database.
 * "Please also add the change temperature & move to database feature in the edit
 * appraisal details section" (Christian Gravias, Monday 13057749471, 17 Sep
 * 2026) — an agent who has opened this modal to fix the details should not have
 * to close it and hunt for the small icons behind it.
 */
const PipelineAppraisalEditModal = ({ isOpen, property, onClose, onSave, onAddToDatabase, onListed, onLost, saving = false }) => {
    const [form, setForm] = useState(null);
    // Call-backs the agent has stacked up but not saved yet. Cleared whenever a
    // different appraisal is opened so a series never lands on the wrong one.
    const [reminders, setReminders] = useState([]);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!property) return;
        setReminders([]);
        setError("");
        // Card shows the sum of all four parking fields as a single "car spaces"
        // number, so pre-fill with that total and write it back to `parkings`.
        const totalParking =
            (Number(property.parkings) || 0) +
            (Number(property.car_ports) || 0) +
            (Number(property.open_spaces) || 0) +
            (Number(property.parking_spaces) || 0) ||
            (Number(property.parking) || 0);

        const hasAmount = property.commission_amount != null && property.commission_amount !== "";

        setForm({
            address: property.address || property.street_name || "",
            suburb: property.suburb || "",
            state: property.state || "",
            postal_code: property.postal_code || property.postcode || "",
            price_display: property.price_display || "exact",
            price: cleanNumber(property.price),
            price_to: cleanNumber(property.price_to),
            weekly_rent: cleanNumber(property.weekly_rent),
            beds: property.beds ?? 0,
            baths: property.baths ?? 0,
            car_spaces: totalParking,
            method_of_sale: property.method_of_sale || "Private Sale",
            auction_date: property.auction_date ? `${formatMelbourneDatePart(property.auction_date)}T${formatMelbourneTimePart(property.auction_date)}` : "",
            agent: property.agent?.id ?? "",
            commission_type: property.commission_type || (hasAmount ? "fixed" : "percentage"),
            commission_rate: property.commission_rate ?? "",
            commission_amount: cleanNumber(property.commission_amount),
            // Cold / Warm / Hot, or "" for an appraisal nobody has graded yet.
            appraisal_temperature: String(property.appraisal_temperature || "").toLowerCase(),
            // off_market / on_market, or "" for one that was never classified
            // (those show on both boards until an agent picks a side).
            appraisal_market_type: String(property.appraisal_market_type || "").toLowerCase(),
            // Always starts blank: this adds a NEW note, it does not edit the
            // existing one shown on the card.
            note: "",
        });
    }, [property]);

    const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
        const response = await getAgents({ page, page_size: pageSize, search });
        const data = response?.data ?? {};
        return {
            results: data.results ?? (Array.isArray(data) ? data : []),
            next: Boolean(data.next),
        };
    }, []);

    if (!isOpen || !form) return null;

    const setField = (name, value) => {
        setError("");
        setForm((prev) => ({ ...prev, [name]: value }));
    };
    const handleChange = (e) => setField(e.target.name, e.target.value);

    // Pre-seed the currently assigned agent so its name shows before the list loads.
    const initialAgentOptions =
        property?.agent?.id != null
            ? [{ id: property.agent.id, name: property.agent.name || property.agent.email || "Agent" }]
            : [];

    const latestNote = property?.latest_note;
    const isAuction = (form.method_of_sale || "").toLowerCase().includes("auction");
    const isRange = form.price_display === "range";
    const priceLabel = isRange ? "Price From" : "Asking Price";

    const currentTemperature = String(property?.appraisal_temperature || "").toLowerCase();
    const temperatureChanged = form.appraisal_temperature !== currentTemperature;

    const currentPipeline = String(property?.appraisal_market_type || "").toLowerCase();
    const pipelineChanged = form.appraisal_market_type !== currentPipeline;

    const handleSubmit = (e) => {
        e.preventDefault();

        const typedNote = (form.note || "").trim();
        // Same rule as the standalone Move dialog: a temperature that changed
        // with no explanation is what the next agent cannot work with.
        if (temperatureChanged && !typedNote) {
            setError("Add a note saying why the temperature changed.");
            return;
        }
        // Moving an appraisal between the two pipelines is the same kind of
        // change: the board it sits on is how the office decides who calls it,
        // so the reason has to be written down too.
        if (pipelineChanged && !typedNote) {
            setError("Add a note saying why it moved pipeline.");
            return;
        }

        const payload = {
            address: form.address,
            suburb: form.suburb,
            state: form.state,
            postal_code: form.postal_code,
            price_display: form.price_display,
            beds: parseInt(form.beds, 10) || 0,
            baths: parseInt(form.baths, 10) || 0,
            // Card sums all parking fields — write the single value to `parkings`
            // and zero the others so the displayed total stays accurate.
            parkings: parseInt(form.car_spaces, 10) || 0,
            parking_spaces: 0,
            car_ports: 0,
            open_spaces: 0,
            method_of_sale: form.method_of_sale || "Private Sale",
            // weekly_rent is a clearable key in patchProperty — send even when empty
            weekly_rent: form.weekly_rent === "" ? null : cleanNumber(form.weekly_rent),
        };

        // Price only applies to exact/range/auction displays.
        if (form.price_display === "exact" || form.price_display === "range" || form.price_display === "auction") {
            payload.price = cleanNumber(form.price) || 0;
        }
        if (isRange && form.price_to !== "") {
            payload.price_to = cleanNumber(form.price_to);
        }

        if (isAuction && form.auction_date) {
            payload.auction_date = datetimeLocalToUtcISOString(form.auction_date);
        }

        // Only send agent when one is selected (blank keeps the existing agent).
        if (form.agent !== "" && form.agent != null) {
            payload.agent = form.agent;
        }

        // Commission — send the type plus the matching value so the backend validates correctly.
        payload.commission_type = form.commission_type;
        if (form.commission_type === "fixed") {
            payload.commission_amount = cleanNumber(form.commission_amount) || 0;
        } else {
            payload.commission_rate = form.commission_rate === "" ? null : form.commission_rate;
        }

        // A note typed here is saved as a new PropertyNote by the backend and
        // becomes the "most recent note" on the pipeline card. When the
        // temperature moved, the move is written in front of it so the Notes tab
        // reads as a history — byte for byte what the Move dialog composes.
        // A pipeline move rides in the same way, so an appraisal that changed
        // both at once records both sentences in front of the one reason.
        const moves = [];
        if (temperatureChanged) {
            payload.appraisal_temperature = form.appraisal_temperature;
            moves.push(
                `Moved from ${temperatureLabel(currentTemperature)} to ${temperatureLabel(form.appraisal_temperature)}.`
            );
        }
        if (pipelineChanged) {
            payload.appraisal_market_type = form.appraisal_market_type;
            moves.push(
                `Moved from ${pipelineLabel(currentPipeline)} to ${pipelineLabel(form.appraisal_market_type)} pipeline.`
            );
        }
        if (moves.length > 0) {
            payload.appraisal_note = `${moves.join(" ")} ${typedNote}`;
        } else if (typedNote) {
            payload.appraisal_note = typedNote;
        }

        // The whole reminder series goes over as one JSON key — patchProperty
        // sends multipart, so it has to be a string. The backend creates one
        // reminder per entry against the owner (or a task on the property).
        if (reminders.length > 0) {
            payload.appraisal_reminders = JSON.stringify(
                reminders.map((entry) => ({
                    date: entry.date.toISOString(),
                    reason: entry.reason,
                }))
            );
        }

        onSave(property.id, payload);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in font-sans text-[#353B48]">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-5 bg-[#F7F9FB] flex items-center justify-between" style={{ borderBottom: "0.45px solid #D3D3D3" }}>
                    <h2 className="text-xl font-bold text-[#1F2937]">Edit Appraisal</h2>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#9CA3AF]">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar">
                    {/* Note + call-back series — first thing in the modal. "Add
                        notes/reminder series to the top of this pencil section,
                        its inefficient being at the bottom and us having to
                        scroll" (Paul Jimoulis, Monday 13234629380, 8 Oct 2026).
                        Logging what happened and when to ring back is why most
                        agents open this modal; the details below rarely change. */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <StickyNote className="w-5 h-5" />
                            <h3 style={headingStyle}>Add a Note</h3>
                        </div>
                        {latestNote?.content && (
                            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                                <StickyNote className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-[12px] text-gray-700 whitespace-pre-wrap break-words">{latestNote.content}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        Last note by {latestNote.author_name || "Unknown"}
                                        {latestNote.created_at ? ` · ${formatMelbourneDateTime(latestNote.created_at)}` : ""}
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="space-y-3 px-1">
                            <label style={labelStyle}>
                                New note {temperatureChanged && <span className="text-red-500">*</span>}
                            </label>
                            <textarea
                                name="note"
                                rows={3}
                                value={form.note}
                                onChange={handleChange}
                                placeholder={
                                    temperatureChanged || pipelineChanged
                                        ? "Why are you moving it? e.g. Owner has decided to list before Christmas."
                                        : "What has happened with this appraisal?"
                                }
                                className={inputClass}
                            />
                        </div>

                        {/* The call-back series sits directly under the note, where
                            Simon Abbott asked for it (Monday 12985995388, 7 Sep
                            2026): the note says what happened, the series says when
                            to ring back about it. */}
                        <div className="px-1">
                            <AppraisalReminderSeries
                                propertyId={property?.id}
                                value={reminders}
                                onChange={setReminders}
                            />
                        </div>
                    </div>

                    {/* Property / Address */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <MapPin className="w-5 h-5" />
                            <h3 style={headingStyle}>Property</h3>
                        </div>
                        <div className="space-y-3 px-1">
                            <label style={labelStyle}>Property Address</label>
                            <input name="address" value={form.address} onChange={handleChange} placeholder="20 Sweyn Street" className={inputClass} />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Suburb</label>
                                <input name="suburb" value={form.suburb} onChange={handleChange} placeholder="Balwyn North" className={inputClass} />
                            </div>
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>State</label>
                                <input name="state" value={form.state} onChange={handleChange} placeholder="Victoria" className={inputClass} />
                            </div>
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Postcode</label>
                                <input name="postal_code" value={form.postal_code} onChange={handleChange} placeholder="3104" className={inputClass} />
                            </div>
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <DollarSign className="w-5 h-5" />
                            <h3 style={headingStyle}>Pricing</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Price Display</label>
                                <select name="price_display" value={form.price_display} onChange={handleChange} className={`${inputClass} appearance-none cursor-pointer`}>
                                    {priceDisplayOptions.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            {form.price_display !== "contact" && (
                                <div className="space-y-3 px-1">
                                    <label style={labelStyle}>{priceLabel}</label>
                                    <input name="price" type="number" min="0" value={form.price} onChange={handleChange} placeholder="0" className={inputClass} />
                                </div>
                            )}
                            {isRange && (
                                <div className="space-y-3 px-1">
                                    <label style={labelStyle}>Price To</label>
                                    <input name="price_to" type="number" min="0" value={form.price_to} onChange={handleChange} placeholder="0" className={inputClass} />
                                </div>
                            )}
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Weekly Rent (for yield)</label>
                                <input name="weekly_rent" type="number" min="0" value={form.weekly_rent} onChange={handleChange} placeholder="Optional" className={inputClass} />
                            </div>
                        </div>
                    </div>

                    {/* Property Details */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <Home className="w-5 h-5" />
                            <h3 style={headingStyle}>Details</h3>
                        </div>
                        <div className="grid grid-cols-3 gap-5">
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Beds</label>
                                <input name="beds" type="number" min="0" value={form.beds} onChange={handleChange} className={inputClass} />
                            </div>
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Baths</label>
                                <input name="baths" type="number" min="0" value={form.baths} onChange={handleChange} className={inputClass} />
                            </div>
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Car Spaces</label>
                                <input name="car_spaces" type="number" min="0" value={form.car_spaces} onChange={handleChange} className={inputClass} />
                            </div>
                        </div>
                    </div>

                    {/* Sale & Assignment */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <Users className="w-5 h-5" />
                            <h3 style={headingStyle}>Sale & Assignment</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Method of Sale</label>
                                <select name="method_of_sale" value={form.method_of_sale} onChange={handleChange} className={`${inputClass} appearance-none cursor-pointer`}>
                                    {methodOptions.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                            {isAuction && (
                                <div className="space-y-3 px-1">
                                    <label style={labelStyle}>Auction Date & Time</label>
                                    <input name="auction_date" type="datetime-local" value={form.auction_date} onChange={handleChange} className={inputClass} />
                                </div>
                            )}
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Primary Agent</label>
                                <InfiniteScrollSelect
                                    value={form.agent}
                                    onChange={(val) => setField("agent", val)}
                                    placeholder="Select Agent"
                                    fetchOptions={fetchAgentOptions}
                                    initialOptions={initialAgentOptions}
                                    pageSize={12}
                                    getLabel={(agent) =>
                                        agent.first_name && agent.last_name
                                            ? `${agent.first_name} ${agent.last_name}`
                                            : agent.name || agent.email
                                    }
                                    getValue={(agent) => agent.id}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pipeline — move the appraisal between the Off Market and
                        On Market boards. "Once an appraisal is loaded as an
                        on-market or off-market appraisal you should be able to
                        move it if things change" (Simon Abbott, CRM-2026-00075,
                        Monday 13099869358). Nothing could write this after the
                        appraisal was created, so an appraisal filed on the wrong
                        board had to be deleted and re-entered. */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <Store className="w-5 h-5" />
                            <h3 style={headingStyle}>Appraisal Pipeline</h3>
                        </div>
                        {/* Labelled "Move to Off Market / On Market" for the same
                            reason the temperature tiles are: "On Market" on its
                            own matches plenty of other text on the page. */}
                        <div className="grid grid-cols-2 gap-2 px-1">
                            {PIPELINES.map((p) => {
                                const isCurrent = p.value === currentPipeline;
                                const isSelected = p.value === form.appraisal_market_type;
                                return (
                                    <button
                                        key={p.value}
                                        type="button"
                                        aria-pressed={isSelected}
                                        aria-label={`Move to ${p.label}`}
                                        onClick={() => setField("appraisal_market_type", p.value)}
                                        className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${
                                            isSelected ? p.active : p.idle
                                        }`}
                                    >
                                        <span className="block text-[14px] font-bold">{p.label}</span>
                                        <span className="block text-[10px] font-medium opacity-80 mt-0.5">
                                            {isCurrent ? "Current" : p.hint}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-[#9CA3AF] px-1">
                            {pipelineChanged
                                ? `Saving moves this appraisal to the ${pipelineLabel(
                                      form.appraisal_market_type
                                  )} Pipeline. Say why in the note above.`
                                : currentPipeline
                                ? `Currently in the ${pipelineLabel(currentPipeline)} Pipeline. Pick the other one to move it when you save.`
                                : "Not classified yet, so it shows on both boards. Pick one to file it."}
                        </p>
                    </div>

                    {/* Temperature — the same Cold / Warm / Hot move the card and
                        the row offer, asked for in here as well (Christian
                        Gravias, Monday 13057749471). Changing it here makes the
                        note above compulsory, exactly as the Move dialog does. */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <Thermometer className="w-5 h-5" />
                            <h3 style={headingStyle}>Appraisal Temperature</h3>
                        </div>
                        {/* Labelled "Move to Cold / Warm / Hot": the tile's own
                            text is not enough to tell it apart from everything
                            else on the page that says Hot. */}
                        <div className="grid grid-cols-3 gap-2 px-1">
                            {TEMPERATURES.map((t) => {
                                const isCurrent = t.value === currentTemperature;
                                const isSelected = t.value === form.appraisal_temperature;
                                return (
                                    <button
                                        key={t.value}
                                        type="button"
                                        aria-pressed={isSelected}
                                        aria-label={`Move to ${t.label}`}
                                        onClick={() => setField("appraisal_temperature", t.value)}
                                        className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${
                                            isSelected ? t.active : t.idle
                                        }`}
                                    >
                                        <span className="block text-[14px] font-bold">{t.label}</span>
                                        <span className="block text-[10px] font-medium opacity-80 mt-0.5">
                                            {isCurrent ? "Current" : t.hint}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-[#9CA3AF] px-1">
                            {temperatureChanged
                                ? `Saving moves this appraisal from ${temperatureLabel(currentTemperature)} to ${temperatureLabel(
                                      form.appraisal_temperature
                                  )}. Say why in the note above.`
                                : `Currently ${temperatureLabel(currentTemperature)}. Pick another to move it to that list when you save.`}
                        </p>
                    </div>

                    {/* Commission */}
                    <div className="bg-[#F9FAFB] rounded-2xl p-6 space-y-5">
                        <div className="flex items-center gap-2 text-[#55C5D0]">
                            <Percent className="w-5 h-5" />
                            <h3 style={headingStyle}>Expected Commission</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-3 px-1">
                                <label style={labelStyle}>Commission Type</label>
                                <select name="commission_type" value={form.commission_type} onChange={handleChange} className={`${inputClass} appearance-none cursor-pointer`}>
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount ($)</option>
                                </select>
                            </div>
                            {form.commission_type === "fixed" ? (
                                <div className="space-y-3 px-1">
                                    <label style={labelStyle}>Commission Amount ($)</label>
                                    <input name="commission_amount" type="number" min="0" value={form.commission_amount} onChange={handleChange} placeholder="0" className={inputClass} />
                                </div>
                            ) : (
                                <div className="space-y-3 px-1">
                                    <label style={labelStyle}>Commission Rate (%)</label>
                                    <input name="commission_rate" type="number" min="0" max="100" step="0.01" value={form.commission_rate} onChange={handleChange} placeholder="0" className={inputClass} />
                                </div>
                            )}
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="px-6 md:px-8 py-5 border-t border-[#F3F4F6] flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#FDFDFD]">
                    {/* Files the vendor back onto a calling list. Deliberately its
                        own action rather than part of Save: it writes a Database
                        record straight away and the appraisal stays on the board. */}
                    <div className="flex flex-wrap items-center gap-3">
                        {onAddToDatabase && (
                            <button
                                type="button"
                                onClick={() => onAddToDatabase(property)}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E4E6E9] text-[#353B48] font-bold rounded-xl hover:bg-gray-50 transition-all text-[14px]"
                            >
                                <Database className="w-4 h-4 text-[#55C5D0]" />
                                Add to Database
                            </button>
                        )}
                        {/* Listed — promote to a draft listing in My Listings. */}
                        {onListed && property?.id && (
                            <button
                                type="button"
                                onClick={() => onListed(property)}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#55C5D0] text-[#2b9aa4] font-bold rounded-xl hover:bg-[#eefafb] transition-all text-[14px]"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                Listed
                            </button>
                        )}
                        {/* Lost — same reason list/effect as the holding area. */}
                        {onLost && property?.id && (
                            <MarkLostMenu
                                onSelect={(reason, note) => onLost(property, reason, note)}
                                align="left"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#E4E6E9] text-[#6B7280] font-bold rounded-xl hover:bg-gray-50 hover:text-[#c8392f] transition-all text-[14px]"
                            />
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-4">
                        {error && <p className="text-[12px] font-bold text-red-500">{error}</p>}
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="px-8 py-3.5 border border-[#E4E6E9] text-[#6B7280] font-bold rounded-xl hover:bg-gray-50 transition-all text-[15px] min-w-[120px] disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="px-10 py-3.5 bg-[#55C5D0] hover:bg-[#45b5c0] text-white font-bold rounded-xl shadow-lg shadow-[#55C5D0]/20 transition-all text-[15px] min-w-[140px] disabled:opacity-60"
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PipelineAppraisalEditModal;
